'use client';

import type { ReactNode, Ref } from 'react';

import {
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  COMPACT_DIALOG_FOOTER_CLASS,
  COMPACT_DIALOG_SHELL_CLASS,
} from '@/components/ui/compact-dialog';
import {
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useDialogFocusReturn } from '@/components/ui/use-dialog-focus-return';
import { cn } from '@/lib/utils';

/**
 * What the shared alert/confirm surfaces render. An alert has no
 * `cancelText`; a confirm always has one.
 */
export type AlertDialogSurfaceView = {
  title: string;
  /** False when the caller gave no title: the fallback stays screen-reader only. */
  showTitle: boolean;
  description: ReactNode;
  body?: ReactNode;
  confirmText: string;
  cancelText?: string;
  destructive: boolean;
};

type SurfaceProps = {
  view: AlertDialogSurfaceView;
  confirmRef?: Ref<HTMLButtonElement>;
  cancelRef?: Ref<HTMLButtonElement>;
  autoFocusConfirm: boolean;
  onOpenAutoFocus?: (event: Event) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Desktop: the compact AlertDialog (16px shell, 8px inset on every side,
 * centred by the primitive). Title names the action, the description names
 * the consequence, and the footer is a plain row with ghost Cancel left and
 * the action right.
 */
export function AlertDialogCompactSurface({
  view,
  confirmRef,
  cancelRef,
  autoFocusConfirm,
  onOpenAutoFocus,
  onConfirm,
  onCancel,
}: SurfaceProps) {
  const focusReturn = useDialogFocusReturn(true, onOpenAutoFocus);
  return (
    <AlertDialogContent
      data-testid="compact-alert-dialog"
      className={cn(
        COMPACT_DIALOG_SHELL_CLASS,
        'shadow-popover border-0 data-[size=default]:sm:max-w-md'
      )}
      {...focusReturn}
    >
      <div className="space-y-1 px-2.5 pt-2">
        <AlertDialogTitle
          className={view.showTitle ? 'type-section-title' : 'sr-only'}
        >
          {view.title}
        </AlertDialogTitle>
        <AlertDialogDescription className="type-body text-muted-foreground whitespace-pre-wrap">
          {view.description}
        </AlertDialogDescription>
      </div>
      {view.body ? <div className="px-2.5">{view.body}</div> : null}
      <div
        data-slot="compact-alert-footer"
        className={cn(COMPACT_DIALOG_FOOTER_CLASS, 'w-full')}
      >
        {view.cancelText ? (
          <Button
            ref={cancelRef}
            autoFocus={!autoFocusConfirm}
            variant="ghost"
            size="sm"
            className="imai-press"
            onClick={onCancel}
          >
            {view.cancelText}
          </Button>
        ) : (
          <span aria-hidden="true" />
        )}
        <Button
          ref={confirmRef}
          autoFocus={autoFocusConfirm}
          size="sm"
          variant={view.destructive ? 'destructive' : 'default'}
          className="imai-press"
          onClick={onConfirm}
        >
          {view.confirmText}
        </Button>
      </div>
    </AlertDialogContent>
  );
}

/**
 * Mobile: the same content in a bottom drawer, with the same 8px inset on
 * every side and full-width touch targets (action above Cancel).
 */
export function AlertDialogDrawerSurface({
  view,
  confirmRef,
  cancelRef,
  autoFocusConfirm,
  onOpenAutoFocus,
  onConfirm,
  onCancel,
}: SurfaceProps) {
  const focusReturn = useDialogFocusReturn(true, onOpenAutoFocus);
  return (
    <DrawerContent
      data-testid="compact-alert-drawer"
      className="gap-2 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
      {...focusReturn}
    >
      <div className="space-y-1 px-2.5 pt-2 text-start">
        <DrawerTitle
          className={
            view.showTitle ? 'type-section-title text-start' : 'sr-only'
          }
        >
          {view.title}
        </DrawerTitle>
        <DrawerDescription className="type-body text-muted-foreground whitespace-pre-wrap">
          {view.description}
        </DrawerDescription>
      </div>
      {view.body ? <div className="px-2.5">{view.body}</div> : null}
      <div className="flex flex-col gap-2">
        <Button
          ref={confirmRef}
          autoFocus={autoFocusConfirm}
          variant={view.destructive ? 'destructive' : 'default'}
          className="imai-press"
          onClick={onConfirm}
        >
          {view.confirmText}
        </Button>
        {view.cancelText ? (
          <Button
            ref={cancelRef}
            autoFocus={!autoFocusConfirm}
            variant="ghost"
            className="imai-press"
            onClick={onCancel}
          >
            {view.cancelText}
          </Button>
        ) : null}
      </div>
    </DrawerContent>
  );
}
